import type { JSX } from 'react';

import { OPT_IN_FEATURES, type FeatureFlag } from '@schema/features.js';

import type { BuilderStore } from '../state.js';

export interface FeaturesEditorProps {
  store: BuilderStore;
}

interface FlagDef {
  value: FeatureFlag;
  label: string;
  description: string;
}

interface Group {
  tier: 'Pro' | 'Enterprise';
  flags: FlagDef[];
}

/**
 * Core / Free tier capabilities. These are always-on for every tenant —
 * no schema flag gates them, so they aren't toggleable here. Listed for
 * visibility so a customer browsing the Features tab understands what
 * they get without upgrading.
 */
const CORE_FEATURES: readonly { label: string; description: string }[] = [
  {
    label: 'Tile grid (app / web / spacer / folder)',
    description: 'Tap-to-launch tiles, multi-app folders, spacers for layout.',
  },
  {
    label: 'Theme — colors + light/dark/auto mode',
    description:
      "Background, primary, accent, surface, text, text-muted. Mode picker honors the device's OS preference.",
  },
  {
    label: 'Built-in tools',
    description: 'Device Info, DNS/Ping, Speed Test, Calculator — surfaced in the Tools modal.',
  },
  {
    label: 'QR provisioning + Devices panel',
    description:
      'Print a QR sticker, scan from the APK / browser. Each launcher boot self-registers; live list in the Portal.',
  },
  {
    label: 'Single profile + status bar',
    description:
      'One profile per tenant on Free (multi-profile is Pro). Configurable status bar (clock, battery, signal).',
  },
  {
    label: 'Friendly error screens',
    description:
      "Fail-to-Core: network outages and bad configs show a retry, never a blank screen.",
  },
];

const GROUPS: readonly Group[] = [
  {
    tier: 'Pro',
    flags: [
      {
        value: 'theming',
        label: 'Theming',
        description: 'Full palette (surface, text, brand), logo, wallpaper, custom font, dark-mode.',
      },
      {
        value: 'multiProfile',
        label: 'Multi-profile',
        description: 'Multiple profiles in one config — admin picks the default; tile + theme per profile.',
      },
      {
        value: 'scheduledProfiles',
        label: 'Scheduled profiles',
        description: 'Time-of-day profile rotation. First-match-wins; falls back to defaultProfileId.',
      },
      {
        value: 'agentActions',
        label: 'Agent actions',
        description: "agentAction tiles dispatch via the device's MDM agent: reboot, runScript, syncNow.",
      },
      {
        value: 'diagnosticOverlay',
        label: 'Diagnostic overlay',
        description:
          'Long-press a corner to reveal device info, license status, telemetry counters — gated behind the admin PIN when one is configured.',
      },
      {
        value: 'broadcasts',
        label: 'Broadcasts',
        description: 'Fleet-wide messages from the Portal’s Broadcasts tab. Devices (and the live preview) poll every 60s and render active items in the launcher’s bell-icon panel.',
      },
      {
        value: 'customTools',
        label: 'Custom tools',
        description: 'Per-tenant entries in the launcher’s Tools modal beyond the built-in Device Info / DNS-Ping. Adds tools[] to the config.',
      },
      {
        value: 'biometricAuth',
        label: 'Biometric admin gesture',
        description: 'When the host bridge exposes a biometric primitive (sideload APK with BiometricPrompt), the long-press admin gesture prompts for fingerprint/face instead of (or before) the PIN dialog. Browsers + bridge-less hosts fall back to PIN.',
      },
      {
        value: 'signageMode',
        label: 'Signage mode',
        description: "Hides the launcher's header + footer chrome and scales the tile grid up for distance viewing. Pair with Multi-profile + Scheduled profiles to auto-rotate content on an unattended display (lobby TV, menu board, drive-thru order screen).",
      },
      // dexDetectionBasic stays in the schema + runtime so existing
      // configs continue to load; hidden from the Builder until DeX-
      // mode UX is something a customer actually asks for.
    ],
  },
  {
    tier: 'Enterprise',
    flags: [
      {
        value: 'remoteConfig',
        label: 'Remote config',
        description: 'Poll an endpoint for live config updates (ETag, last-known-good fallback).',
      },
      {
        value: 'telemetryExport',
        label: 'Telemetry export',
        description: 'Forward events to a sink — retry, age-out, secret-ref auth tokens.',
      },
      {
        value: 'ssoDisplay',
        label: 'SSO display',
        description: 'Show the current user (from MDM identity) in the status bar.',
      },
      {
        value: 'auditLog',
        label: 'Audit log',
        description: 'Record admin gestures, PIN entries, overlay opens for compliance review.',
      },
      // dexDetectionNative — same story as dexDetectionBasic: hidden
      // until a customer asks. Schema + runtime + bridge stay live.
    ],
  },
  // OEM-specific feature flags (oem.zebra / oem.samsung / oem.honeywell)
  // stay in the schema and runtime so existing configs continue to work,
  // but are hidden from the Builder UI until each integration path is
  // certified on physical hardware with a beta partner. Restore the
  // 'Enterprise · OEM' group here when certification lands.
];

/**
 * `enabledFeatures` editor.
 *
 * The schema accepts any flag at every tier — that's deliberate, so the
 * Builder can preview a Pro/Enterprise theme even from a Free license.
 * The on-device runtime intersects with what the license entitles, so
 * a "Pro feature on a Free device" silently no-ops.
 *
 * This editor surfaces the canonical list, grouped by tier, with a one-
 * line description of what each flag actually changes when honored. Bulk
 * 'Enable all in tier' is the fast path for builder users who want to
 * see the whole feature surface lit up at once.
 */
export function FeaturesEditor({ store }: FeaturesEditorProps): JSX.Element {
  const enabled = new Set(store.config.enabledFeatures);

  function toggle(flag: FeatureFlag): void {
    const next = new Set(enabled);
    if (next.has(flag)) next.delete(flag);
    else next.add(flag);
    store.patchEnabledFeatures([...next]);
  }

  // Bulk "Enable all" deliberately skips opt-in-only flags (e.g. signageMode):
  // turning on "everything" shouldn't flip the launcher into a mode the
  // customer never asked for. They stay individually toggleable below.
  function setGroup(group: Group, on: boolean): void {
    const next = new Set(enabled);
    for (const f of group.flags) {
      if (on) {
        if (!OPT_IN_FEATURES.has(f.value)) next.add(f.value);
      } else next.delete(f.value);
    }
    store.patchEnabledFeatures([...next]);
  }

  function setAll(on: boolean): void {
    if (on) {
      const next = new Set(enabled);
      for (const g of GROUPS) {
        for (const f of g.flags) {
          if (!OPT_IN_FEATURES.has(f.value)) next.add(f.value);
        }
      }
      store.patchEnabledFeatures([...next]);
    } else {
      store.patchEnabledFeatures([]);
    }
  }

  // Flags reachable by bulk "Enable all" (everything except opt-in modes).
  // Drives the disabled state so the button greys out once they're all on,
  // even though an un-ticked signageMode keeps the raw count below total.
  const bulkFlags = GROUPS.flatMap((g) => g.flags.map((f) => f.value)).filter(
    (v) => !OPT_IN_FEATURES.has(v),
  );
  const allBulkOn = bulkFlags.every((v) => enabled.has(v));

  const totalCount = GROUPS.reduce((n, g) => n + g.flags.length, 0);
  // Count only enabled flags that are CURRENTLY displayed — hidden
  // flags (e.g., OEM, DeX) shouldn't inflate the "X / Y" counter and
  // make the number look broken.
  const visibleFlags = new Set(GROUPS.flatMap((g) => g.flags.map((f) => f.value)));
  const enabledVisible = [...enabled].filter((f) => visibleFlags.has(f as FeatureFlag)).length;

  return (
    <div className="editor">
      <header className="editor__head">
        <h2 className="editor__title">Features</h2>
        <p className="editor__sub">
          Toggle which features the launcher honors. Schema accepts any flag at
          every tier so you can preview locked Pro/Enterprise behavior on a Free
          license; on-device the runtime intersects with what the license entitles.{' '}
          <a href="https://mobilauncher.com/docs/tiers" target="_blank" rel="noopener noreferrer" className="editor__doc-link">
            Read the docs →
          </a>
        </p>
      </header>

      {/* Core / Free tier — always-on capabilities, listed for visibility
          but not toggleable. These ship with every tenant regardless
          of license, so there's no flag to flip. */}
      <fieldset className="editor__group editor__group--readonly">
        <legend className="editor__group-label">Core (Free) · always on</legend>
        <ul className="event-checklist">
          {CORE_FEATURES.map((f) => (
            <li key={f.label} className="event-checklist__row">
              <span className="event-checklist__name">{f.label}</span>
              <span className="event-checklist__desc">{f.description}</span>
            </li>
          ))}
        </ul>
      </fieldset>

      <fieldset className="editor__group">
        <legend className="editor__group-label">
          Optional flags enabled ({enabledVisible} / {totalCount})
        </legend>
        <div className="features-bulk">
          <button
            type="button"
            className="add-tile__btn"
            onClick={() => setAll(true)}
            disabled={allBulkOn}
          >
            Enable all
          </button>
          <button
            type="button"
            className="add-tile__btn"
            onClick={() => setAll(false)}
            disabled={enabledVisible === 0}
          >
            Disable all
          </button>
        </div>
      </fieldset>

      {GROUPS.map((g) => {
        const groupEnabled = g.flags.filter((f) => enabled.has(f.value)).length;
        // "Enable all {tier}" greys out once every BULK flag in the group is
        // on — opt-in flags (signageMode) don't count, since the button
        // never turns them on.
        const allBulkOnInGroup = g.flags
          .filter((f) => !OPT_IN_FEATURES.has(f.value))
          .every((f) => enabled.has(f.value));
        const noneOn = groupEnabled === 0;
        return (
          <fieldset key={g.tier} className="editor__group">
            <legend className="editor__group-label">
              {g.tier} ({groupEnabled} / {g.flags.length})
            </legend>
            <div className="features-bulk">
              <button
                type="button"
                className="add-tile__btn"
                onClick={() => setGroup(g, true)}
                disabled={allBulkOnInGroup}
              >
                Enable all {g.tier}
              </button>
              <button
                type="button"
                className="add-tile__btn"
                onClick={() => setGroup(g, false)}
                disabled={noneOn}
              >
                Disable all
              </button>
            </div>
            <ul className="event-checklist">
              {g.flags.map((f) => {
                const isOn = enabled.has(f.value);
                return (
                  <li key={f.value} className="event-checklist__row">
                    <label className="event-checklist__label">
                      <input
                        type="checkbox"
                        className="event-checklist__check"
                        checked={isOn}
                        onChange={() => toggle(f.value)}
                        data-testid={`feature-toggle-${f.value}`}
                      />
                      <span className="event-checklist__name">{f.label}</span>
                    </label>
                    <span className="event-checklist__desc">
                      <code className="features__flag">{f.value}</code> — {f.description}
                      {OPT_IN_FEATURES.has(f.value) && (
                        <em> Opt-in — not included in “Enable all”; tick it explicitly.</em>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </fieldset>
        );
      })}
    </div>
  );
}
